import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { stripHtml } from '../../../utils/sanitize';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

interface ContactFilteredTicketsTableProps {
  visible: boolean;
  filterLabel: string;
  filterType: string;
  onClose: () => void;
}

const stateLabels: Record<string, string> = {
  new: 'New',
  open: 'Open',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  awaiting_info: 'Awaiting Info',
  solution_proposed: 'Solution Proposed',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  resolved: 'Resolved',
  auto_resolved: 'Auto Resolved',
  closed: 'Closed',
  closed_complete: 'Closed Complete',
  closed_incomplete: 'Closed Incomplete',
  cancelled: 'Cancelled',
  skipped: 'Skipped',
};

const changeStateLabels: Record<string, string> = {
  new: 'New',
  assess: 'Assess',
  authorize: 'Authorize',
  scheduled: 'Scheduled',
  implement: 'Implement',
  review: 'Review',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const problemStateLabels: Record<string, string> = {
  new: 'New',
  assess_1: 'Assess 1',
  assess_2: 'Assess 2',
  root_cause_analysis: 'Root Cause Analysis',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const priorityLabels: Record<string, string> = {
  critical_1: 'Critical',
  high_2: 'High',
  moderate_3: 'Moderate',
  low_4: 'Low',
  planning_5: 'Planning',
};

const ticketTypeLabels: Record<string, string> = {
  CASE: 'Case',
  INCIDENT: 'Incident',
  SERVICE_REQUEST: 'Service Request',
  CHANGE: 'Change',
  PROBLEM: 'Problem',
};

const ContactFilteredTicketsTable: React.FC<ContactFilteredTicketsTableProps> = ({
  visible,
  filterLabel,
  filterType,
  onClose,
}) => {
  const navigate = useNavigate();
  const { isContact } = useAuth();
  const { formatDateTime } = useDateFormatter();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Build query params
  const queryParams = new URLSearchParams();
  queryParams.append('filterType', filterType);
  queryParams.append('page', String(page));
  queryParams.append('limit', String(pageSize));

  const { data, isLoading, error } = useQuery({
    queryKey: ['contact-filtered-tickets', filterType, page, pageSize],
    queryFn: async () => {
      const response = await api.get(`/dashboard/contact-tickets?${queryParams}`);
      return response.data;
    },
    enabled: visible,
  });

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [filterType, pageSize]);

  if (!visible) return null;

  // Navigate to ticket detail - contacts go to /tickets/:id, others go to specific routes
  const handleRowClick = (ticket: any) => {
    if (isContact()) {
      navigate(`/tickets/${ticket.id}?from=dashboard`);
    } else {
      // For non-contact users, navigate to type-specific routes
      switch (ticket.ticketType) {
        case 'INCIDENT':
          navigate(`/incidents/${ticket.id}`);
          break;
        case 'SERVICE_REQUEST':
          navigate(`/service-requests/${ticket.id}`);
          break;
        case 'CHANGE':
          navigate(`/changes/${ticket.id}`);
          break;
        case 'PROBLEM':
          navigate(`/problems/${ticket.id}`);
          break;
        case 'CASE':
        default:
          navigate(`/cases/${ticket.id}`);
          break;
      }
    }
  };

  // Get state label based on ticket type
  const getStateLabel = (ticket: any): string => {
    if (ticket.ticketType === 'CHANGE' && ticket.changeState) {
      return changeStateLabels[ticket.changeState] || ticket.changeState;
    }
    if (ticket.ticketType === 'PROBLEM' && ticket.problemState) {
      return problemStateLabels[ticket.problemState] || ticket.problemState;
    }
    return stateLabels[ticket.state] || ticket.state;
  };

  return (
    <div className="bg-white rounded-lg shadow mt-6">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {filterLabel}
            {data?.total ? ` (${data.total} tickets)` : ''}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full"
        >
          <XMarkIcon className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {isLoading && (
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
          <p className="mt-2 text-sm text-gray-500">Loading tickets...</p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 text-red-700">
          Failed to load tickets. Please try again.
        </div>
      )}

      {data && !isLoading && (
        <>
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Short Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.tickets.map((ticket: any) => (
                  <tr
                    key={ticket.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRowClick(ticket)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-primary-600">{ticket.number}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        ticket.ticketType === 'INCIDENT' ? 'bg-red-100 text-red-800' :
                        ticket.ticketType === 'SERVICE_REQUEST' ? 'bg-blue-100 text-blue-800' :
                        ticket.ticketType === 'CHANGE' ? 'bg-green-100 text-green-800' :
                        ticket.ticketType === 'PROBLEM' ? 'bg-amber-100 text-amber-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {ticketTypeLabels[ticket.ticketType] || ticket.ticketType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={stripHtml(ticket.shortDescription)}>
                      {stripHtml(ticket.shortDescription) || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {ticket.customerName || <span className="text-gray-400 italic">-</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        ticket.priority === 'critical_1' ? 'bg-red-100 text-red-800' :
                        ticket.priority === 'high_2' ? 'bg-orange-100 text-orange-800' :
                        ticket.priority === 'moderate_3' ? 'bg-yellow-100 text-yellow-800' :
                        ticket.priority === 'low_4' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {priorityLabels[ticket.priority] || ticket.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {getStateLabel(ticket)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {ticket.assignedTo || <span className="text-gray-400 italic">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatDateTime(ticket.createdAt)}
                    </td>
                  </tr>
                ))}
                {data.tickets.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                      No tickets found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-2 bg-gray-50">
            {data?.total > 0 ? (
              <>
                {/* Left: Records per page dropdown */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((size, index) => {
                      const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                      const isDisabled = previousSize >= data.total && size > data.total;
                      return (
                        <option key={size} value={size} disabled={isDisabled}>
                          {size}
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-sm text-gray-700">entries</span>
                </div>

                {/* Center: Entry count */}
                <div className="text-sm text-gray-700">
                  Showing {Math.min((page - 1) * pageSize + 1, data.total)} to {Math.min(page * pageSize, data.total)} of {data.total} entries
                </div>

                {/* Right: Pagination buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="First page"
                  >
                    &laquo;
                  </button>
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  {/* Page numbers */}
                  {(() => {
                    const totalPages = Math.ceil(data.total / pageSize);
                    const pages: (number | string)[] = [];

                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      if (page <= 3) {
                        pages.push(1, 2, 3, 4, '...', totalPages);
                      } else if (page >= totalPages - 2) {
                        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                      } else {
                        pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                      }
                    }

                    return pages.map((p, idx) => (
                      p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setPage(p as number)}
                          className={`px-3 py-1 text-sm border rounded-md ${
                            page === p
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {p}
                        </button>
                      )
                    ));
                  })()}

                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= Math.ceil(data.total / pageSize)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setPage(Math.ceil(data.total / pageSize))}
                    disabled={page >= Math.ceil(data.total / pageSize)}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Last page"
                  >
                    &raquo;
                  </button>
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-500 w-full text-center">No records</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ContactFilteredTicketsTable;
